<template>
  <div class="au-form">
    <!-- Order summary -->
    <div class="au-summary">
      <div class="sum-grid">
        <div class="sum-row">
          <span class="sum-label">Receipt</span>
          <span class="sum-value mono upper">{{ item.receiptId }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Order ID</span>
          <span class="sum-value mono break">{{ item.merchantOrderId }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Customer</span>
          <span class="sum-value">{{ item.customerName || '—' }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Assigned bank</span>
          <span class="sum-value">{{ item.validatorUsername || '—' }}</span>
        </div>
        <div v-if="item.customerUPIID" class="sum-row">
          <span class="sum-label">Client UPI</span>
          <span class="sum-value mono">{{ item.customerUPIID }}</span>
        </div>
        <div v-if="isAdmin && item.validatorUPIID" class="sum-row">
          <span class="sum-label">Assigned UPI</span>
          <span class="sum-value mono break">{{ item.validatorUPIID }}</span>
        </div>
        <div v-if="item.customerUtr" class="sum-row">
          <span class="sum-label">Customer UTR</span>
          <span class="sum-value mono">{{ item.customerUtr }}</span>
        </div>
        <div class="sum-row">
          <span class="sum-label">Created</span>
          <span class="sum-value">{{ formatDate }}</span>
        </div>
      </div>
      <div class="au-amount-block">
        <div class="amount-label">Amount</div>
        <div class="amount-value">₹{{ formatMoney(getAmount) }}</div>
      </div>
    </div>

    <!-- Editable amount -->
    <div class="au-edit">
      <div class="field" :class="{ 'field-invalid': !editAmount.isValid }">
        <label>Edit amount</label>
        <div class="input-wrap">
          <span class="input-prefix">₹</span>
          <input
            type="text"
            v-model.trim="editAmount.val"
            class="input has-prefix"
            placeholder="Edit amount"
            @focus="clearValidity('editAmount')"
          />
        </div>
        <p v-if="!editAmount.isValid" class="field-error">{{ editAmount.msg }}</p>
      </div>
    </div>

    <!-- Method tabs -->
    <div class="au-method">
      <div class="method-tabs" role="tablist">
        <button
          type="button"
          class="method-tab"
          :class="{ 'is-active': selectedButton === 'utr' }"
          @click.stop="selectButton('utr')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <rect x="3" y="6" width="18" height="12" rx="2" />
            <path d="M3 10h18M7 15h4" stroke-linecap="round" />
          </svg>
          Enter UTR
        </button>
        <button
          type="button"
          class="method-tab"
          :class="{ 'is-active': selectedButton === 'screenshot' }"
          @click.stop="selectButton('screenshot')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Upload screenshot
        </button>
      </div>

      <div v-if="selectedButton === ''" class="method-hint">
        Select a method to add the UTR.
      </div>

      <!-- UTR tab -->
      <div v-else-if="selectedButton === 'utr'" class="method-panel">
        <div class="field" :class="{ 'field-invalid': !customerUtr.isValid }">
          <label>Customer UTR <span class="req">*</span></label>
          <div class="input-wrap">
            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <rect x="3" y="6" width="18" height="12" rx="2" />
              <path d="M3 10h18M7 15h4" stroke-linecap="round" />
            </svg>
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="22"
              class="input mono has-icon"
              placeholder="Enter UTR (digits only)"
              v-model="customerUtr.val"
              @input="filterUtrInput"
              @focus="clearValidity('customerUtr')"
            />
          </div>
          <p v-if="!customerUtr.isValid" class="field-error">{{ customerUtr.msg }}</p>
        </div>

        <div class="panel-actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isLoading"
            @click.stop="addCustomerUtr"
          >
            <span v-if="!isLoading">Submit UTR</span>
            <span v-else class="btn-loading">
              <span class="spinner-mini"></span>
              Submitting…
            </span>
          </button>
        </div>
      </div>

      <!-- Screenshot tab -->
      <div v-else-if="selectedButton === 'screenshot'" class="method-panel">
        <input
          ref="uploadScreenshot"
          class="hidden-input"
          id="uploadScreenshot"
          @change="handleUploadScreenshot"
          type="file"
          accept="image/*"
        />

        <div
          v-if="!uploadScreenshotPreview"
          class="drop-zone"
          :class="{ 'is-invalid': !uploadScreenshot.isValid, 'is-dragging': isDraggingScreenshot }"
          @click.stop="onPickFile('uploadScreenshot')"
          @dragover.prevent="isDraggingScreenshot = true"
          @dragleave.prevent="isDraggingScreenshot = false"
          @drop.prevent="handleScreenshotDrop"
        >
          <div class="drop-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M12 3v14M6 9l6-6 6 6M4 21h16" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="drop-copy">
            <div class="drop-title">Click to upload payment receipt</div>
            <div class="drop-sub">JPG, JPEG, PNG · max 2MB</div>
          </div>
        </div>

        <div v-else class="preview-block">
          <div class="preview-frame">
            <img
              class="preview-img"
              :src="uploadScreenshotPreview"
              @click.stop="onPickFile('uploadScreenshot')"
              title="Click to replace"
            />
            <button type="button" class="preview-remove" @click.stop="cancelUploadedScreenshot" title="Remove">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" />
              </svg>
            </button>
          </div>
          <div class="preview-hint">Click image to replace.</div>
        </div>

        <p v-if="!uploadScreenshot.isValid" class="field-error">{{ uploadScreenshot.msg }}</p>

        <div class="panel-actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isLoading"
            @click.stop="submitUploadScreenshot"
          >
            <span v-if="!isLoading">Submit screenshot</span>
            <span v-else class="btn-loading">
              <span class="spinner-mini"></span>
              Submitting…
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Blocking loader while API in flight -->
    <div v-if="isLoading" class="au-loading-veil">
      <div class="veil-spinner"></div>
    </div>
  </div>
</template>

<script>
import http from "../../http-common.js";

export default {
  name: "add-utr-form",
  props: ["item"],
  emits: ["form-response"],
  data() {
    return {
      selectedButton: "",
      editAmount: {
        val: this.item.amount,
        isValid: true,
        msg: "",
      },
      customerUtr: {
        val: "",
        isValid: true,
        msg: "",
      },
      uploadScreenshotPreview: null,
      uploadScreenshot: {
        val: null,
        isValid: true,
        msg: "",
      },
      isDraggingScreenshot: false,
      formIsValid: true,
      isLoading: false,
    };
  },
  computed: {
    getAmount() {
      if (this.item.amount) {
        if (this.item.txnFee) {
          return this.item.amount;
        } else {
          return parseInt(this.item.amount);
        }
      } else {
        return "";
      }
    },
    isAdmin() {
      return !!this.$store.getters.isAdmin;
    },
    isClient() {
      return !!this.$store.getters.isClient;
    },
    formatDate() {
      if (!this.item.createdAt) return "";
      const date = new Date(this.item.createdAt);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? "pm" : "am";
      return `${day}-${month}-${year} ${hours % 12}:${minutes}:${seconds} ${ampm}`;
    },
  },
  methods: {
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    // Strip non-digit characters as the operator types / pastes.
    // Mirrors the customer-side payment-page filter so the operator
    // can't accidentally enter a Unicode lookalike copied from a
    // customer message or screenshot OCR.
    filterUtrInput(event) {
      const raw = event.target.value;
      const cleaned = raw.replace(/[^0-9]/g, '');
      if (cleaned !== raw) {
        event.target.value = cleaned;
        this.customerUtr.val = cleaned;
      }
    },
    validateForm(formName) {
      this.formIsValid = true;
      if (this.editAmount.val === "") {
        this.editAmount.isValid = false;
        this.editAmount.msg = "Edit amount must not be empty!";
        this.formIsValid = false;
      }
      if (formName == 'enterUtr') {
        // Belt-and-suspenders re-strip before submit.
        this.customerUtr.val = String(this.customerUtr.val || '').replace(/[^0-9]/g, '');
        if (this.customerUtr.val == "") {
          this.customerUtr.msg = "You must enter UTR (digits only)";
          this.customerUtr.isValid = false;
          this.formIsValid = false;
        }
      }
      if (formName == 'uploadScreenshot') {
        if (this.uploadScreenshot.val === null) {
          this.uploadScreenshot.isValid = false;
          this.uploadScreenshot.msg = "You must upload a screenshot.";
          this.formIsValid = false;
        }
      }
    },
    async addCustomerUtr() {
      this.validateForm('enterUtr');

      if (!this.formIsValid) {
        return;
      }

      try {
        this.isLoading = true;
        let response;
        if (this.isClient) {
          // Client uses its own authenticated endpoint (supports add + update)
          response = await http.post(`/orders/${this.item.refID}/client-utr`, { customerUtr: this.customerUtr.val });
        } else {
          response = await http.post(`/subadmin/${this.item.refID}/addCustomerUtr`, { edit_amount: this.editAmount.val, customerUtr: this.customerUtr.val });
        }
        if (response.status == 200 || response.status == 201) {
          if (response.data.status) {
            let data = {
              refID: this.item.refID,
              customerUtr: this.customerUtr.val,
            };
            this.$store.commit("payin/updateCustomerUtr", data);
            if (response.data.data?.paymentStatus == 'approved') {
              data.transactionID = this.customerUtr.val;
              this.$store.commit("payin/setPaymentStatusAndUtr", data);
            }
            if (!this.isClient && this.editAmount.val != this.item.amount) {
              this.$store.commit("payin/updateAmountAndStatus", {
                refID: this.item.refID,
                amount: this.editAmount.val,
                paymentStatus: response.data.data?.paymentStatus,
              });
            }
          }
          this.$emit("form-response", response.data.status, response.data.message);
        } else {
          console.log(response);
        }
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.$emit("form-response", false, error.response?.data?.message || error.message || "Something failed!");
      }
    },
    selectButton(btnName) {
      this.selectedButton = btnName;
    },
    handleUploadScreenshot(event) {
      this.uploadScreenshot.isValid = true;
      this.uploadScreenshot.msg = "";
      const file = event.target.files[0];
      this.acceptScreenshotFile(file);
    },
    handleScreenshotDrop(event) {
      this.isDraggingScreenshot = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (files && files.length > 0) {
        this.acceptScreenshotFile(files[0]);
      }
    },
    acceptScreenshotFile(file) {
      if (!file) return;
      if (!file.type.includes('image/')) {
        this.uploadScreenshot.isValid = false;
        this.uploadScreenshot.msg = 'Only JPG, JPEG and PNG files are allowed.';
      } else if (file.size > 2 * 1024 * 1024) {
        this.uploadScreenshot.isValid = false;
        this.uploadScreenshot.msg = 'File size must be less than 2MB.';
      } else {
        this.uploadScreenshot.isValid = true;
        this.uploadScreenshot.msg = '';
        this.uploadScreenshot.val = file;
        this.uploadScreenshotPreview = URL.createObjectURL(file);
      }
    },
    cancelUploadedScreenshot() {
      this.uploadScreenshot.val = null;
      this.uploadScreenshotPreview = null;
      if (this.$refs.uploadScreenshot) this.$refs.uploadScreenshot.value = null;
    },
    onPickFile(ref) {
      this.$refs[ref].click();
    },
    async submitUploadScreenshot() {
      this.validateForm('uploadScreenshot');

      if (!this.formIsValid) {
        return;
      }

      const formData = new FormData();
      formData.append('upload_screenshot', this.uploadScreenshot.val);
      formData.append('edit_amount', this.editAmount.val);

      this.isLoading = true;

      try {
        const response = await http.post(
          `subadmin/${this.item.refID}/upload-screenshot`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        if (response.status == 201) {
          if (response.data.status) {
            let data = {
              refID: this.item.refID,
              customerUtr: response.data.data.customerUtr,
              uploadScreenshot: response.data.data.upload_screenshot,
            };
            this.$store.commit("payin/updateCustomerUtrAndAttachment", data);
            if (response.data.data.paymentStatus == 'approved') {
              data.transactionID = response.data.data.customerUtr;
              this.$store.commit("payin/setPaymentStatusAndUtr", data);
            }
            if (this.editAmount.val != this.item.amount) {
              this.$store.commit("payin/updateAmountAndStatus", {
                refID: this.item.refID,
                amount: this.editAmount.val,
                paymentStatus: response.data.data.paymentStatus,
              });
            }
          }
          this.$emit("form-response", response.data.status, response.data.message);
        } else {
          alert('failed', 'Failed to create the ticket. Please try again!');
        }
      } catch (error) {
        console.log(error);
      }

      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.au-form {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.au-summary {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  padding: 14px;
  background: #fbfaf7;
  border: 1px solid #ede9df;
  border-radius: 12px;
  align-items: center;
}
.sum-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 20px;
  min-width: 0;
}
.sum-row {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 8px;
  align-items: baseline;
  font-size: 12.5px;
  min-width: 0;
}
.sum-label { color: #94a3b8; font-weight: 500; }
.sum-value { color: #1b2a47; font-weight: 600; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.sum-value.mono { font-family: "SFMono-Regular", Menlo, monospace; font-size: 11.5px; color: #334155; letter-spacing: 0.02em; }
.sum-value.upper { text-transform: uppercase; }
.sum-value.break { word-break: break-all; overflow-wrap: anywhere; }

.au-amount-block {
  padding: 10px 16px;
  background: linear-gradient(135deg, #1B2A47, #2C3E63);
  border-radius: 12px;
  color: #fff;
  text-align: right;
  box-shadow: 0 6px 16px -8px rgba(27, 42, 71, 0.55);
  min-width: 160px;
}
.amount-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  font-weight: 700;
}
.amount-value {
  font-size: 20px;
  font-weight: 800;
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

/* ---------- Field ---------- */
.field { display: flex; flex-direction: column; }
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}
.req { color: #dc2626; }
.input-wrap { position: relative; display: flex; align-items: center; }
.input-prefix {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.input-icon {
  position: absolute;
  left: 12px;
  width: 16px; height: 16px;
  color: #94a3b8;
  pointer-events: none;
}
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #fbfaf7;
  color: #0f172a;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.input.has-prefix { padding-left: 26px; }
.input.has-icon { padding-left: 38px; }
.input.mono { font-family: "SFMono-Regular", Menlo, monospace; letter-spacing: 0.05em; }
.input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 4px rgba(27, 42, 71, 0.08);
}
.field-invalid .input {
  border-color: #fca5a5;
  background: #fef2f2;
}
.field-error {
  font-size: 12px;
  color: #dc2626;
  margin: 6px 0 0;
  font-weight: 500;
}

/* ---------- Method tabs ---------- */
.au-method {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid #f4f0e5;
}
.method-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.method-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.method-tab svg { width: 15px; height: 15px; opacity: 0.7; }
.method-tab:hover { border-color: #cbd5e1; background: #fbfaf7; color: #1b2a47; }
.method-tab.is-active {
  background: #eef2f7;
  border-color: #1B2A47;
  color: #1B2A47;
}
.method-tab.is-active svg { opacity: 1; }
.method-hint {
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
  padding: 8px 0;
}
.method-panel { display: flex; flex-direction: column; gap: 10px; }

/* ---------- Screenshot drop zone ---------- */
.hidden-input { display: none; }
.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 24px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  background: #fbfaf7;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.drop-zone:hover { border-color: #94a3b8; background: #f4f0e5; }
.drop-zone.is-dragging { border-color: #1B2A47; background: #eef2f7; }
.drop-zone.is-invalid { border-color: #fca5a5; background: #fef2f2; }
.drop-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: #fff;
  color: #64748b;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #e2e8f0;
}
.drop-icon svg { width: 20px; height: 20px; }
.drop-title { font-size: 12.5px; font-weight: 600; color: #1b2a47; }
.drop-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }

/* Preview */
.preview-block { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.preview-frame {
  position: relative;
  display: inline-block;
  padding: 6px;
  background: #fff;
  border: 1px solid #a7f3d0;
  border-radius: 10px;
}
.preview-img {
  max-height: 160px;
  max-width: 100%;
  display: block;
  border-radius: 6px;
  cursor: pointer;
}
.preview-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 26px; height: 26px;
  border-radius: 999px;
  background: #dc2626;
  color: #fff;
  border: 2px solid #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.preview-remove:hover { transform: scale(1.08); }
.preview-remove svg { width: 12px; height: 12px; }
.preview-hint { font-size: 11px; color: #94a3b8; }

/* ---------- Panel actions ---------- */
.panel-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
  font-family: inherit;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 14px -6px rgba(27, 42, 71, 0.45);
  min-width: 160px;
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px -8px rgba(27, 42, 71, 0.55);
}
.btn-loading { display: inline-flex; align-items: center; gap: 8px; }
.spinner-mini {
  width: 12px; height: 12px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}

/* ---------- Loading veil ---------- */
.au-loading-veil {
  position: absolute;
  inset: -8px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
}
.veil-spinner {
  width: 32px; height: 32px;
  border-radius: 999px;
  border: 3px solid #e2e8f0;
  border-top-color: #1B2A47;
  animation: spin 0.9s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .au-summary { grid-template-columns: 1fr; }
  .sum-grid { grid-template-columns: 1fr; }
  .au-amount-block { text-align: center; min-width: 0; }
  .method-tabs { grid-template-columns: 1fr; }
}
</style>
